import { useEffect, useRef, useState } from "react";
import { groesse, zeitAusText, zeitmarke } from "../../lib/medien";

/**
 * Ansicht einer Datei der Content-Ablage (0155) mit Kommentaren: Video mit Zeitmarken auf der Leiste (Klick springt hin),
 * Bild mit Punkten (Klick ins Bild setzt einen Punkt), PDF als Link. Wer kommentieren darf, bekommt das Eingabefeld; die
 * Zeitmarke übernimmt es aus dem Video (anhalten, schreiben, senden) oder aus dem Text („0:12 – Logo größer“).
 * Dieselbe Ansicht nutzen Agentur, Creator-Portal und der Freigabe-Link der Marke.
 */

export interface MedienKommentar {
  id: string;
  time_ms: number | null;
  x: number | null;
  y: number | null;
  body: string;
  side: "staff" | "talent" | "brand";
  name?: string | null;
  resolved?: boolean;
  at?: string;
}

export interface MedienDatei {
  id: string;
  name: string;
  kind: "video" | "image" | "pdf";
  size: number;
  sha256?: string | null;
  duration_ms?: number | null;
  width?: number | null;
  height?: number | null;
}

type L = (de: string, en: string) => string;

export function MedienAnsicht({ datei, url, poster, kommentare, onKommentar, onErledigt, t, seiten, kompakt = false, startMs, onLadefehler, hinweis }: {
  datei: MedienDatei;
  url: string | null;
  poster?: string | null;
  kommentare: readonly MedienKommentar[];
  /** ohne: nur lesen */
  onKommentar?: (k: { body: string; time_ms: number | null; x: number | null; y: number | null }) => Promise<boolean>;
  onErledigt?: (id: string, erledigt: boolean) => void;
  t: L;
  /** Beschriftung der Absender (Team, Creator, Marke) */
  seiten: Record<MedienKommentar["side"], string>;
  kompakt?: boolean;
  startMs?: number | null;
  /** Datei ließ sich nicht abspielen bzw. anzeigen (z. B. Link abgelaufen): neuen Link holen */
  onLadefehler?: () => void;
  /** Satz unter dem Eingabefeld, z. B. wer den Kommentar sieht */
  hinweis?: string;
}) {
  const [bildFehler, setBildFehler] = useState(false);
  const video = useRef<HTMLVideoElement>(null);
  const [zeit, setZeit] = useState(0);
  const [dauer, setDauer] = useState<number>(datei.duration_ms ?? 0);
  const [text, setText] = useState("");
  const [punkt, setPunkt] = useState<{ x: number; y: number } | null>(null);
  const [mitZeit, setMitZeit] = useState(true);
  const [busy, setBusy] = useState(false);
  const [aktiv, setAktiv] = useState<string | null>(null);

  useEffect(() => {
    if (startMs != null && video.current) video.current.currentTime = startMs / 1000;
  }, [startMs, url]);

  const springe = (k: MedienKommentar) => {
    setAktiv(k.id);
    if (k.time_ms != null && video.current) {
      video.current.currentTime = k.time_ms / 1000;
      video.current.pause();
    }
  };
  const sortiert = [...kommentare].sort((a, b) => (a.time_ms ?? -1) - (b.time_ms ?? -1) || (a.at ?? "").localeCompare(b.at ?? ""));
  const nummer = new Map(sortiert.map((k, i) => [k.id, i + 1]));
  const absenden = async () => {
    const body = text.trim();
    if (!body || !onKommentar) return;
    const ausText = zeitAusText(body);
    const time = datei.kind === "video" ? (ausText ?? (mitZeit ? Math.round(zeit * 1000) : null)) : null;
    setBusy(true);
    const ok = await onKommentar({ body, time_ms: time, x: punkt?.x ?? null, y: punkt?.y ?? null });
    setBusy(false);
    if (ok) {
      setText("");
      setPunkt(null);
    }
  };

  return (
    <div className={`co-medien${kompakt ? " co-medien-kompakt" : ""}`} data-art={datei.kind}>
      <div className="co-medien-buehne">
        {datei.kind === "video" && (
          <>
            {url ? (
              <video ref={video} src={url} poster={poster ?? undefined} controls playsInline preload="metadata" onError={() => onLadefehler?.()}
                onTimeUpdate={(e) => setZeit(e.currentTarget.currentTime)}
                onLoadedMetadata={(e) => { if (Number.isFinite(e.currentTarget.duration)) setDauer(Math.round(e.currentTarget.duration * 1000)); }}
                aria-label={datei.name} />
            ) : (
              <div className="co-medien-leer">{poster ? <img src={poster} alt="" /> : null}<span>{t("Video wird geladen …", "Loading video …")}</span></div>
            )}
            {dauer > 0 && kommentare.some((k) => k.time_ms != null) && (
              <div className="co-medien-marken" aria-label={t("Kommentare auf der Zeitleiste", "Comments on the timeline")}>
                {sortiert.filter((k) => k.time_ms != null).map((k) => (
                  <button key={k.id} type="button" className={`co-marke co-seite-${k.side}${k.resolved ? " erledigt" : ""}${aktiv === k.id ? " aktiv" : ""}`}
                    style={{ left: `${Math.min(100, (k.time_ms! / dauer) * 100)}%` }} onClick={() => springe(k)}
                    title={`${zeitmarke(k.time_ms!)} – ${k.body}`} aria-label={`${zeitmarke(k.time_ms!)}: ${k.body}`}>
                    {nummer.get(k.id)}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
        {datei.kind === "image" && (
          <div className="co-medien-bild" onClick={(e) => {
            if (!onKommentar) return;
            const r = e.currentTarget.getBoundingClientRect();
            setPunkt({ x: Math.round(((e.clientX - r.left) / r.width) * 10000) / 10000, y: Math.round(((e.clientY - r.top) / r.height) * 10000) / 10000 });
          }}>
            {url && !bildFehler ? <img src={url} alt={datei.name} onError={() => { setBildFehler(true); onLadefehler?.(); }} />
              : url && bildFehler ? (
                <div className="co-medien-leer co-medien-pdf">
                  <span>{t("Dieses Bild kann der Browser nicht anzeigen (z. B. HEIC). Bitte herunterladen.", "The browser cannot show this image (e.g. HEIC). Please download it.")}</span>
                  <a className="btn" href={url} target="_blank" rel="noopener noreferrer">{t("Öffnen", "Open")}</a>
                </div>
              ) : <div className="co-medien-leer"><span>{t("Bild wird geladen …", "Loading image …")}</span></div>}
            {sortiert.filter((k) => k.x != null && k.y != null).map((k) => (
              <button key={k.id} type="button" className={`co-pin co-seite-${k.side}${k.resolved ? " erledigt" : ""}${aktiv === k.id ? " aktiv" : ""}`}
                style={{ left: `${k.x! * 100}%`, top: `${k.y! * 100}%` }} onClick={(e) => { e.stopPropagation(); setAktiv(k.id); }}
                title={k.body} aria-label={k.body}>{nummer.get(k.id)}</button>
            ))}
            {punkt && <span className="co-pin neu" style={{ left: `${punkt.x * 100}%`, top: `${punkt.y * 100}%` }} aria-hidden="true">+</span>}
          </div>
        )}
        {datei.kind === "pdf" && (
          <div className="co-medien-leer co-medien-pdf">
            <strong>PDF</strong>
            {url ? <a className="btn" href={url} target="_blank" rel="noopener noreferrer">{t("PDF öffnen", "Open PDF")}</a> : <span>{t("Wird geladen …", "Loading …")}</span>}
          </div>
        )}
      </div>
      <div className="co-medien-seite">
      <div className="co-medien-info">
        <span className="co-medien-name">{datei.name}</span>
        <span className="muted">{groesse(datei.size)}{datei.duration_ms ? ` · ${zeitmarke(datei.duration_ms)}` : ""}{datei.width && datei.height ? ` · ${datei.width}×${datei.height}` : ""}</span>
        {datei.sha256 && <code className="co-sha" title="SHA-256">SHA-256 {datei.sha256.slice(0, 12)}…</code>}
      </div>
      <ol className="co-kommentare" aria-label={t("Kommentare", "Comments")}>
        {sortiert.map((k) => (
          <li key={k.id} className={`co-seite-${k.side}${k.resolved ? " erledigt" : ""}${aktiv === k.id ? " aktiv" : ""}`}>
            <button type="button" className="co-kommentar-zeit" onClick={() => springe(k)} disabled={k.time_ms == null && k.x == null}>
              <span className="co-nr">{nummer.get(k.id)}</span>
              {k.time_ms != null ? zeitmarke(k.time_ms) : k.x != null ? t("Punkt", "Point") : "–"}
            </button>
            <div>
              <p>{k.body}</p>
              <small className="muted">{k.name ? `${k.name} · ` : ""}{seiten[k.side]}{k.resolved ? ` · ${t("erledigt", "done")}` : ""}</small>
            </div>
            {onErledigt && (
              <button type="button" className="co-erledigt" onClick={() => onErledigt(k.id, !k.resolved)} aria-pressed={!!k.resolved}>
                {k.resolved ? t("Wieder öffnen", "Reopen") : t("Erledigt", "Done")}
              </button>
            )}
          </li>
        ))}
        {!sortiert.length && <li className="muted co-kein">{t("Noch keine Kommentare.", "No comments yet.")}</li>}
      </ol>
      {onKommentar && (
        <form className="co-kommentar-neu stack" onSubmit={(e) => { e.preventDefault(); void absenden(); }}>
          <label>
            {datei.kind === "video"
              ? (mitZeit ? t(`Kommentar bei ${zeitmarke(zeit * 1000)}`, `Comment at ${zeitmarke(zeit * 1000)}`) : t("Kommentar zum ganzen Video", "Comment on the whole video"))
              : datei.kind === "image" ? (punkt ? t("Kommentar zum Punkt im Bild", "Comment on the point in the image") : t("Kommentar (ins Bild tippen setzt einen Punkt)", "Comment (tap the image to set a point)"))
              : t("Kommentar", "Comment")}
            <textarea value={text} maxLength={2000} rows={2} onChange={(e) => setText(e.target.value)}
              onFocus={() => video.current?.pause()}
              placeholder={datei.kind === "video" ? t("z. B. Logo größer, Ton leiser …", "e.g. bigger logo, lower volume …") : t("Was soll sich ändern?", "What should change?")} />
          </label>
          <div className="row">
            {datei.kind === "video" && (
              <label className="tk-check"><input type="checkbox" checked={mitZeit} onChange={(e) => setMitZeit(e.target.checked)} /> {t("mit Zeitmarke", "with timestamp")}</label>
            )}
            {punkt && <button type="button" onClick={() => setPunkt(null)}>{t("Punkt entfernen", "Remove point")}</button>}
            <button type="submit" className="primary" disabled={busy || !text.trim()}>{t("Kommentar senden", "Send comment")}</button>
          </div>
          {hinweis && <small className="muted">{hinweis}</small>}
        </form>
      )}
      </div>
    </div>
  );
}
